import React, { ReactNode, useState, useContext, useEffect } from "react";
import { Rules } from "../api/rules/Rules.domain";
import { RulesModel } from "../api/rules/RulesModel";
import { AppConfigContext } from "./AppConfigContext";
import { useAlert } from "../hooks/useAlert";
import { useAuth } from "./controllers/useAuth";
import { useNavigate } from "react-router-dom";

interface RulesContextType {
  rules?: Rules;
}

export const RulesContext = React.createContext<RulesContextType>(null!);

interface RulesProviderProps {
  children: ReactNode;
}

export function RulesProvider({ children }: RulesProviderProps) {
  const appConfig = useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { getToken, logoutUnauthorized } = useAuth();
  const navigate = useNavigate();
  const rulesModel = new RulesModel(appConfig!);

  const [rules, setRules] = useState<Rules>();
  const [isLoading, setIsLoading] = useState(true);

  const handleUnauthorized = () => {
    logoutUnauthorized();
    navigate("/login");
  };

  useEffect(() => {
    const loadRules = async () => {
      try {
        const token = getToken();
        if (!token) throw new Error("Le jeton d'authentification est manquant.");

        const response = await rulesModel.getRules(token);

        if (response.status === 401) {
          handleUnauthorized();
          return;
        }

        if (response.status !== 200 || !response.data) {
          throw new Error(`Échec du chargement des règles : HTTP ${response.status}`);
        }

        setRules(response.data);
      } catch (err) {
        if (err instanceof Error) {
          showAlert(err.message, "danger");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadRules();
  }, [appConfig, getToken]);

  if (isLoading) {
    return null;
  }

  return <RulesContext.Provider value={{ rules }}>{children}</RulesContext.Provider>;
}
